import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import {
  client,
  NewCommentFile,
  FormFilesDocumentTypes,
  TaskComment,
  TaskEvent,
  OutboundTaskTypeKey,
  FileType,
  OutboundTaskType,
} from '../../../../api';
import { useContext, useRef, useState } from 'react';
import { TaskContext } from '../../contexts';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import { FormLabel } from '../../../FormLabel';
import Button from 'design-system/src/components/Button/Button';
import { useNavigate } from 'react-router-dom';
import { NoDocumentationAttachedWarningModal } from '../../NoDocumentationAttachedWarningModal/NoDocumentationAttachedWarningModal';
import SubmitWithoutInfoWarningModal from '../../SubmitWithoutInfoWarningModal/SubmitWithoutInfoWarningModal';
import DeclineModal from '../../DeclineModal';
import HoldTaskModal from '../../HoldTaskModal/HoldTaskModal';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { FileUploadField } from '../../shared';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton';
import { ResponseInput } from '../../../tableItemDetailViewComponents/FormComponents';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import { validateFiles } from '../../../../utils';
import { formatRemainingTimeText, shouldShowExpirationCountdown } from '../../../../utils';
import type { PaInformationRequestTask, WithAdditionalTaskResponseFields } from '../../types';
import { paGenerationNotice, sponsoredTaskCopyWithPaNotice } from '../../../businessRelationshipNotices/copy';
import HelpIcon from 'design-system/src/assets/icons/helpCircleOutline.svg?react';
import { StructuredTaskViewWrapper } from '..';

/**
 * Version of TaskView that is used for PA information requests.
 */
const PaInformationRequestTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<PaInformationRequestTask>;
}> = ({ taskResponse }) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [showNoDocumentationWarningModal, setShowNoDocumentationWarningModal] = useState(false);
  const [showSubmitWithoutInfoWarningModal, setShowSubmitWithoutInfoWarningModal] = useState(false);
  const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false);
  const [showHoldTaskModal, setShowHoldTaskModal] = useState(false);
  const [showHelpRequestModal, setShowHelpRequestModal] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [newFilesUploaded, setNewFilesUploaded] = useState<NewCommentFile[]>([]);
  const [fileValidationError, setFileValidationError] = useState<string | null>(null);

  const { taskId, onTaskUpdate, updating, setUpdating } = useContext(TaskContext);
  const navigate = useNavigate();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const onSubmit = () => {
    if (newFilesUploaded.length || showNoDocumentationWarningModal || showSubmitWithoutInfoWarningModal) {
      submit.mutate({});
      return;
    }

    if (!inputValue.trim() && !showSubmitWithoutInfoWarningModal) {
      setShowSubmitWithoutInfoWarningModal(true);
      return;
    }

    setShowNoDocumentationWarningModal(true);
  };

  const submit = useMutation<void, Error, { ehrSubmission?: string; noInfoReason?: string }>({
    mutationFn: async ({ ehrSubmission, noInfoReason } = {}) => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "submit PA information request" for task ID: ${taskId}`);
        return;
      }
      // TODO: move all this into a util or something
      const formData = new FormData();
      formData.append('ehr_submission', ehrSubmission || '');

      // Signals to the backend that the message is a no-info reason,
      // not a regular information response.
      if (noInfoReason !== undefined) {
        formData.append('message', noInfoReason);
        formData.append('submit_without_info', 'true');
      } else {
        formData.append('message', inputValue);
      }

      if (newFilesUploaded.length) {
        const formFilesDocumentTypes: FormFilesDocumentTypes = {};

        newFilesUploaded.forEach((fileInfo) => {
          formData.append('files[]', fileInfo.file);

          // We can't add the document type to the file directly,
          // so we track the types in a separate object
          // and use the file names as keys.
          formFilesDocumentTypes[fileInfo.file.name] = fileInfo.document_type;
        });

        formData.append('form_files_document_types', JSON.stringify(formFilesDocumentTypes));
      }

      const res = await client.post(`tasks/${taskId}/submit-pa-information-request`, formData);
      return res.data;
    },
    onMutate: () => {
      setUpdating(true);
    },
    onSuccess: () => {
      onTaskUpdate();
      setNewFilesUploaded([]);
      setInputValue('');
      navigate('/tasks');
      toast.custom(() => <Toast variant="success">Response submitted successfully</Toast>);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      setUpdating(false);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const holdTask = useMutation<void, Error, string>({
    mutationFn: async (reason: string) => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "hold task" for task ID: ${taskId}`);
        return;
      }
      const formData = new FormData();
      formData.append('hold_reason', reason);
      const res = await client.post(`tasks/${taskId}/hold-denial-pred-expiring-task`, formData);
      return res.data;
    },
    onMutate: () => {
      setUpdating(true);
    },
    onSuccess: () => {
      onTaskUpdate();
      setUpdating(false);
      setShowHoldTaskModal(false);
      toast.custom(() => <Toast variant="success">Task held successfully</Toast>);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      setUpdating(false);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const handleFileUpload = (newFiles: NewCommentFile[]) => {
    setFileValidationError(null);
    const validationError = validateFiles(newFiles);
    if (validationError) {
      setFileValidationError(validationError);
      return;
    }

    // Check for duplicate filenames
    for (const fileToUpload of newFiles) {
      const existingFile = newFilesUploaded.find((f) => f.file.name === fileToUpload.file.name);
      if (existingFile) {
        setFileValidationError(
          `A file with the name "${fileToUpload.file.name}" has already been added. Please rename the file and try again.`,
        );
        return;
      }
    }

    setNewFilesUploaded([...newFilesUploaded, ...newFiles]);
  };

  const handleFileDelete = (filename: string, documentType: FileType) => {
    const updatedFiles = newFilesUploaded.filter(
      (fileInfo) => !(fileInfo.file.name === filename && fileInfo.document_type === documentType),
    );
    setNewFilesUploaded(updatedFiles);
  };

  const { drug, patient, prescriber, task } = taskResponse;

  const getBusinessRelationshipNoticeCopy = (taskKey: OutboundTaskTypeKey, hasDenialLikelyQuestions?: boolean) => {
    return taskKey === OutboundTaskTypeKey.PA_INFORMATION_REQUEST_UNMET_CRITERIA || hasDenialLikelyQuestions
      ? sponsoredTaskCopyWithPaNotice
      : paGenerationNotice;
  };

  const isCompleted = task.status === 'completed';

  if (!task.timeline || !task.timeline.length) {
    Sentry.captureMessage('No initial comment found for PA Information Request task', {
      level: 'error',
      extra: {
        taskId,
      },
    });
    return <PageLoadError />;
  }

  const initialComment = task.timeline[0] as TaskComment | undefined;

  // Get completion event for completed tasks
  let completionEvent: TaskEvent | undefined;
  if (isCompleted) {
    if (task.timeline.length === 1 || !initialComment) {
      Sentry.captureMessage('No completion event found for PA Information Request task', {
        level: 'error',
        extra: {
          taskId,
        },
      });
    } else {
      const timelineItem = task.timeline[1];
      completionEvent = timelineItem.item_type === 'event' ? (timelineItem as TaskEvent) : undefined;
    }
  }

  const autoSubmitCountdown = shouldShowExpirationCountdown(
    task.task_key,
    task.denial_pred_expiration_metadata,
    task.status,
  ) ? (
    <InfoBox variant="info" includeIcon={false} noTopMargin className="!px-4 !py-3">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Typography styledAs="bodySmallSpaceGrotesk" weight={500}>
            PA auto-submits in {formatRemainingTimeText(task.denial_pred_expiration_metadata!.expired_at)}
          </Typography>
          <Tooltip
            content="To prevent prescription delays, we automatically submit PAs after 5 days without the requested information."
            childNotInteractive
          >
            <HelpIcon className="size-4 shrink-0 cursor-help text-inherit" aria-hidden />
          </Tooltip>
        </div>
        <Button
          variant="link"
          size="sm"
          onClick={() => setShowHoldTaskModal(true)}
          disabled={holdTask.isPending}
          className="text-[var(--grayscale-900)]"
        >
          Disable auto-submit
        </Button>
      </div>
    </InfoBox>
  ) : undefined;

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={task.type as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        aboveHeadingContent={autoSubmitCountdown}
        showBusinessRelationshipSmallPrint
        sidebar={{
          drug: {
            data: drug,
            showBusinessRelationshipNotice: true,
            businessRelationshipNoticeCopy: getBusinessRelationshipNoticeCopy(
              task.task_key,
              task.has_denial_likely_questions,
            ),
          },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        {/* Completion message for completed tasks */}
        {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

        {/* Instructions */}
        <div className="mb-6 flex flex-col gap-4">
          <TaskInstructionSummary>
            Please respond to the question(s) below and provide supporting documentation, if applicable:
          </TaskInstructionSummary>
          {initialComment && initialComment.item_type === 'comment' && (
            <Typography styledAs="bodyExtraSmallDMSans">
              <RenderHtmlString>{initialComment.message}</RenderHtmlString>
            </Typography>
          )}
        </div>

        {!isCompleted && (
          <div className="mt-6 flex flex-col gap-4 rounded-medium border border-solid border-grayscale-100 p-4">
            <div className="flex flex-col border-b-grayscale-100 pb-4 border-b-solid">
              <FormLabel htmlFor="pa-response-input">
                {task.is_pharma_contract_task ? 'Provide suggested information' : 'Provide required information'}
              </FormLabel>
              <ResponseInput
                id="pa-response-input"
                ref={inputRef}
                placeholder="Please add any additional information or context"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="!min-h-[60px]"
              />
            </div>

            <FileUploadField
              label="Additional documents"
              labelId="additional-documents-label"
              instructions="Please upload any relevant visit notes or supporting clinical documentation."
              uploadedFiles={newFilesUploaded}
              onFileDelete={handleFileDelete}
              onFilesUpload={handleFileUpload}
              fileTypesOptions={['Lab Results', 'Medical Records', 'Other']}
              errors={[
                {
                  message: fileValidationError || '',
                  visible: !!fileValidationError,
                },
              ]}
            />

            <ButtonRow align={Alignment.SpaceBetween} fullWidth className="border-t-grayscale-100 pt-4 border-t-solid">
              <ButtonRow className="!gap-4">
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => setShowCancelConfirmationModal(true)}
                  disabled={submit.isPending}
                >
                  Cancel prescription
                </Button>
                <OtherRequestButton setShowNewFollowupTaskModal={setShowHelpRequestModal} />
              </ButtonRow>
              <Button
                variant="primary"
                onClick={onSubmit}
                size="sm"
                disabled={submit.isPending}
                isLoading={submit.isPending}
              >
                {inputValue.trim() || newFilesUploaded.length ? 'Submit' : 'Submit PA with missing info'}
              </Button>
            </ButtonRow>
          </div>
        )}
      </StructuredTaskViewWrapper>

      {showNoDocumentationWarningModal && (
        <NoDocumentationAttachedWarningModal
          task={task}
          updating={updating}
          onSubmit={(ehrSubmission?: string) => submit.mutate({ ehrSubmission })}
          onClose={() => setShowNoDocumentationWarningModal(false)}
        />
      )}

      {showSubmitWithoutInfoWarningModal && (
        <SubmitWithoutInfoWarningModal
          updating={updating}
          onSubmit={(noInfoReason?: string) => submit.mutate({ noInfoReason })}
          onClose={() => setShowSubmitWithoutInfoWarningModal(false)}
        />
      )}

      {showCancelConfirmationModal && (
        <DeclineModal
          closeModal={() => setShowCancelConfirmationModal(false)}
          patient={patient}
          drug={drug}
          taskKey={task.task_key}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}

      {showHoldTaskModal && (
        <HoldTaskModal
          onClose={() => setShowHoldTaskModal(false)}
          onSubmit={holdTask.mutate}
          isSubmitting={holdTask.isPending}
        />
      )}

      {showHelpRequestModal && (
        <NewFollowupTaskModal
          closeModal={() => setShowHelpRequestModal(false)}
          patient={patient}
          drug={drug}
          patientId={patient.id}
          prescriptionId={drug.id}
          taskId={taskId}
          relatedTaskKey={task.task_key as OutboundTaskTypeKey}
          relatedTaskType={task.type as OutboundTaskType}
        />
      )}
    </>
  );
};

export default PaInformationRequestTaskView;
